/**
 * Entry-point рантайм-бандла plp-widgets.
 *
 * Регистрирует кастомные элементы (`<plp-form>`, `<plp-field>`, `<plp-slider>`,
 * `<plp-tabs>`/`<plp-tab>`, `<plp-countdown>`, `<plp-map>`, `<plp-video>`),
 * запускает enhancers (data-pl-* атрибуты) и сервисы (tracking-context).
 *
 * Lazy-зависимости (phone, map, video) загружаются по требованию через
 * `core/lazy.loadChunk()`.
 */

import { VERSION } from './core/version.js';
import { isEditMode, onEditModeChange } from './core/edit-mode.js';
import { loadChunk, isChunkLoaded } from './core/lazy.js';
import { PlpForm } from './form/plp-form.js';
import { PlpField } from './form/plp-field.js';
import { snapshot as snapshotTracking } from './form/tracking-context.js';
import { PlpSlider } from './widgets/plp-slider.js';
import { PlpTab, PlpTabs } from './widgets/plp-tabs.js';
import { PlpCountdown } from './widgets/plp-countdown.js';
import { PlpMap } from './widgets/plp-map.js';
import { PlpVideo } from './widgets/plp-video.js';
import { initEnhancers } from './enhancers/scanner.js';

declare global {
  interface Window {
    __plpWidgets?: {
      version: string;
      loaded: true;
      isEditMode: typeof isEditMode;
      onEditModeChange: typeof onEditModeChange;
      loadChunk: typeof loadChunk;
      isChunkLoaded: typeof isChunkLoaded;
    };
  }
}

function defineOnce(name: string, ctor: CustomElementConstructor): void {
  if (!customElements.get(name)) {
    customElements.define(name, ctor);
  }
}

/**
 * Базовые стили для кастомных тегов. Без них браузер по умолчанию даёт
 * `display: inline` (как для unknown HTML elements), что ломает box-модель
 * (формы/слайдеры схлопываются, outline'ы в edit-режиме рисуются на union
 * inline-fragment'ов). Объявляем `display: block` через `:where()` чтобы
 * специфичность была 0 — пользователь спокойно переопределит Tailwind'ом
 * (`class="inline-block"` и т.п.).
 */
function injectBaseStyles(): void {
  if (document.getElementById('__plp-widgets-base-css')) return;
  const style = document.createElement('style');
  style.id = '__plp-widgets-base-css';
  // 1) display:block через :where() — спецификация 0, юзер переопределит
  //    Tailwind-классом (`class="inline-block"` и т.п.).
  // 2) Reset border/padding/appearance на тегах с атрибутом `type=...` —
  //    нужен чтобы перебить @tailwindcss/forms plugin, чей селектор
  //    `[type='text']` (спец. 10) попадает на сам `<plp-field type="text">`
  //    и навешивает border/padding/background. Селектор ниже специфичнее
  //    (тег + атрибут = 11), поэтому работает без !important.
  // 3) Вертикальный ритм формы по умолчанию: 1rem между прямыми потомками
  //    <plp-form> (поля, submit-кнопка, microcopy). Без этого форма без
  //    авторского `space-y-*` схлопывается, а кнопка прижимается к последнему
  //    полю. `margin-top` (а не flex `gap`) — то же свойство, что у Tailwind
  //    `space-y-*`, поэтому авторский `space-y-6`/`mt-2` перебивает его, а не
  //    суммируется (gap сложился бы со `space-y` и дал двойной отступ).
  //    Специфичность подобрана окном (0,0,2): `:not(head)` семантически no-op
  //    (прямой потомок формы никогда не <head>), но поднимает селектор над
  //    preflight/normalize `button,p{margin:0}` (0,0,1) — иначе он обнулил бы
  //    отступ у нативной кнопки и microcopy (у `<plp-field>` этой проблемы нет,
  //    его тип-селектор preflight не трогает). Класс автора (0,1,0) всё равно
  //    выигрывает — ритм остаётся переопределяемым.
  style.textContent =
    ':where(plp-form,plp-field,plp-slider,plp-tabs,plp-countdown,plp-map,plp-video){display:block}' +
    ':where(plp-tab){display:block}' +
    ':where(plp-tab[hidden]){display:none}' +
    'plp-form > * + *:not(head){margin-top:1rem}' +
    'plp-field[type],plp-form[type],plp-tab[type]{appearance:none;-webkit-appearance:none;' +
    'background:transparent;border:0;padding:0;font:inherit;color:inherit;border-radius:0}' +
    '.plp-form-success-dialog{border:0;border-radius:20px;padding:0;max-width:min(92vw,440px);' +
    'width:100%;color:#111827;box-shadow:0 24px 80px rgba(15,23,42,.28)}' +
    '.plp-form-success-dialog::backdrop{background:rgba(15,23,42,.55);backdrop-filter:blur(2px)}' +
    '.plp-form-success-dialog__panel{padding:28px;text-align:center;background:#fff}' +
    '.plp-form-success-dialog__title{margin:0;font:700 24px/1.25 system-ui,sans-serif}' +
    '.plp-form-success-dialog__message{margin:12px 0 0;font:400 16px/1.5 system-ui,sans-serif;color:#4b5563}' +
    '.plp-form-success-dialog__button{margin-top:24px;border:0;border-radius:12px;padding:11px 24px;' +
    'background:#111827;color:#fff;font:600 15px/1.3 system-ui,sans-serif;cursor:pointer}' +
    '.plp-form-success-dialog__button:focus-visible{outline:3px solid #93c5fd;outline-offset:3px}' +
    '.plp-commerce-modal-open{overflow:hidden}' +
    '.plp-commerce-dialog{width:min(96vw,720px);height:min(92vh,900px);padding:0;border:0;' +
    'border-radius:20px;overflow:hidden;background:#fff;box-shadow:0 24px 80px rgba(15,23,42,.35)}' +
    '.plp-commerce-dialog::backdrop{background:rgba(15,23,42,.62);backdrop-filter:blur(3px)}' +
    '.plp-commerce-dialog__frame{display:block;width:100%;height:100%;border:0;background:#fff}' +
    '.plp-commerce-dialog__close{position:absolute;z-index:2;top:10px;right:10px;width:36px;height:36px;' +
    'border:0;border-radius:999px;background:rgba(15,23,42,.78);color:#fff;font:24px/1 system-ui;' +
    'cursor:pointer;box-shadow:0 2px 10px rgba(15,23,42,.22)}' +
    '.plp-commerce-dialog__close:focus-visible{outline:3px solid #93c5fd;outline-offset:2px}' +
    '.plp-commerce-resume{position:fixed;z-index:2147483000;right:max(20px,env(safe-area-inset-right));' +
    'bottom:max(20px,env(safe-area-inset-bottom));display:flex;align-items:center;gap:14px;' +
    'width:min(430px,calc(100vw - 32px));padding:18px 18px 18px 20px;border:1px solid rgba(15,23,42,.12);' +
    'border-radius:18px;background:#fff;color:#111827;box-shadow:0 18px 54px rgba(15,23,42,.2);' +
    'font-family:system-ui,-apple-system,sans-serif;box-sizing:border-box}' +
    '.plp-commerce-resume--bottom-left{right:auto;left:max(20px,env(safe-area-inset-left))}' +
    '.plp-commerce-resume[data-theme="dark"]{border-color:rgba(255,255,255,.15);background:#111827;color:#fff}' +
    '.plp-commerce-resume__content{display:flex;min-width:0;flex:1;flex-direction:column;gap:4px}' +
    '.plp-commerce-resume__heading{font-size:15px;line-height:1.35;font-weight:700}' +
    '.plp-commerce-resume__summary,.plp-commerce-resume__more{font-size:13px;line-height:1.4;opacity:.72}' +
    '.plp-commerce-resume__action{flex:none;border:0;border-radius:11px;padding:11px 14px;background:#111827;' +
    'color:#fff;font:600 13px/1.25 system-ui,-apple-system,sans-serif;cursor:pointer}' +
    '.plp-commerce-resume[data-theme="dark"] .plp-commerce-resume__action{background:#fff;color:#111827}' +
    '.plp-commerce-resume__close{position:absolute;top:-9px;right:-9px;width:26px;height:26px;border:0;' +
    'border-radius:999px;background:#64748b;color:#fff;font:18px/1 system-ui;cursor:pointer}' +
    '.plp-commerce-resume__action:focus-visible,.plp-commerce-resume__close:focus-visible{' +
    'outline:3px solid #93c5fd;outline-offset:2px}' +
    '@media(prefers-color-scheme:dark){.plp-commerce-resume[data-theme="auto"]{border-color:rgba(255,255,255,.15);' +
    'background:#111827;color:#fff}.plp-commerce-resume[data-theme="auto"] .plp-commerce-resume__action{' +
    'background:#fff;color:#111827}}' +
    '@media(max-width:640px){.plp-commerce-resume,.plp-commerce-resume--bottom-left{right:16px;left:16px;' +
    'bottom:max(12px,env(safe-area-inset-bottom));width:auto;padding:14px 14px 14px 16px;border-radius:15px}' +
    '.plp-commerce-resume__heading{font-size:14px}.plp-commerce-resume__summary{display:none}' +
    '.plp-commerce-resume__action{padding:10px 12px;font-size:12px}}' +
    '.plp-commerce-pending-dialog{width:min(92vw,460px);padding:28px;border:0;border-radius:20px;' +
    'background:#fff;color:#111827;box-shadow:0 24px 80px rgba(15,23,42,.32);font-family:system-ui,sans-serif}' +
    '.plp-commerce-pending-dialog::backdrop{background:rgba(15,23,42,.62);backdrop-filter:blur(3px)}' +
    '.plp-commerce-pending-dialog__title{margin:0;font-size:22px;line-height:1.25;font-weight:700}' +
    '.plp-commerce-pending-dialog__text{margin:10px 0 0;color:#475569;font-size:15px;line-height:1.5}' +
    '.plp-commerce-pending-dialog__actions{display:flex;flex-wrap:wrap;gap:10px;margin-top:24px}' +
    '.plp-commerce-pending-dialog__primary,.plp-commerce-pending-dialog__secondary{border:0;border-radius:11px;' +
    'padding:11px 16px;font:600 14px/1.3 system-ui;cursor:pointer}' +
    '.plp-commerce-pending-dialog__primary{background:#111827;color:#fff}' +
    '.plp-commerce-pending-dialog__secondary{background:#e2e8f0;color:#111827}' +
    '.plp-commerce-pending-dialog__close{position:absolute;top:12px;right:14px;border:0;background:transparent;' +
    'color:#64748b;font:25px/1 system-ui;cursor:pointer}' +
    '.plp-commerce-pending-dialog button:focus-visible{outline:3px solid #93c5fd;outline-offset:2px}' +
    // Spinner animation для <plp-form> submit-кнопки во время отправки.
    '@keyframes plp-spin{to{transform:rotate(360deg)}}';
  (document.head ?? document.documentElement).appendChild(style);
}

injectBaseStyles();

defineOnce('plp-form', PlpForm);
defineOnce('plp-field', PlpField);
defineOnce('plp-slider', PlpSlider);
defineOnce('plp-tabs', PlpTabs);
defineOnce('plp-tab', PlpTab);
defineOnce('plp-countdown', PlpCountdown);
defineOnce('plp-map', PlpMap);
defineOnce('plp-video', PlpVideo);

// UTM/referrer/landing — first-touch снапшот сразу при загрузке бандла,
// не дожидаясь рендера форм. Если на странице нет форм — снапшот
// просто полежит в localStorage до следующего визита.
snapshotTracking();

// Data-attribute enhancers (lightbox, anchor, reveal, copy).
initEnhancers();

// Маркер загрузки + публичные утилиты для интеграций (creator preview-iframe,
// devtools, smoke-тесты).
window.__plpWidgets = {
  version: VERSION,
  loaded: true,
  isEditMode,
  onEditModeChange,
  loadChunk,
  isChunkLoaded,
};
